/**
 * PromotionPerkEditor — Define perks (Free Addon, Free Shipping, Custom Label)
 *
 * Each perk has a stable local ID for React keys.
 * Perks can be assigned to tier levels by name in PromotionTierTable.
 *
 * Part of Plan 199, Epic 02.
 */

import { useCallback } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface PerkRow {
  localId: string;
  perk_name: string;
  perk_type: "Free Addon" | "Free Shipping" | "Custom Label";
  linked_addon_field: string;
  description: string;
}

const PERK_TYPES = [
  { value: "Free Addon", label: "Free Addon" },
  { value: "Free Shipping", label: "Free Shipping" },
  { value: "Custom Label", label: "Custom Label" },
] as const;

interface Props {
  perks: PerkRow[];
  onChange: (perks: PerkRow[]) => void;
}

export function PromotionPerkEditor({ perks, onChange }: Props) {
  const updatePerk = useCallback(
    (index: number, field: keyof PerkRow, value: string) => {
      const next = [...perks];
      next[index] = { ...next[index], [field]: value };
      onChange(next);
    },
    [perks, onChange]
  );

  const addPerk = useCallback(() => {
    onChange([
      ...perks,
      {
        localId: crypto.randomUUID(),
        perk_name: "",
        perk_type: "Free Addon",
        linked_addon_field: "",
        description: "",
      },
    ]);
  }, [perks, onChange]);

  const removePerk = useCallback(
    (index: number) => {
      onChange(perks.filter((_, i) => i !== index));
    },
    [perks, onChange]
  );

  return (
    <div className="space-y-3">
      {perks.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No perks defined. Add perks like "Free Embroidery" or "Free Shipping"
          to assign to tier levels.
        </p>
      )}

      {perks.map((perk, index) => (
        <div
          key={perk.localId}
          className="rounded-xl border border-border p-3 space-y-2"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              {/* Row 1: Name + Type */}
              <div className="flex gap-2">
                <Input
                  value={perk.perk_name}
                  onChange={(e) =>
                    updatePerk(index, "perk_name", e.target.value)
                  }
                  placeholder="Perk name (e.g., Free Embroidery)"
                  className="h-9 text-sm flex-1"
                />
                <Select
                  value={perk.perk_type}
                  onValueChange={(v) => updatePerk(index, "perk_type", v)}
                >
                  <SelectTrigger className="h-9 w-[150px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERK_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Linked Addon Field (only for Free Addon) */}
              {perk.perk_type === "Free Addon" && (
                <Input
                  value={perk.linked_addon_field}
                  onChange={(e) =>
                    updatePerk(index, "linked_addon_field", e.target.value)
                  }
                  placeholder="Linked personalization field label"
                  className="h-9 text-sm"
                />
              )}

              {/* Description */}
              <Input
                value={perk.description}
                onChange={(e) =>
                  updatePerk(index, "description", e.target.value)
                }
                placeholder="Description shown to shoppers (optional)"
                className="h-9 text-sm"
              />
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 flex-shrink-0 text-muted-foreground hover:text-red-500"
              onClick={() => removePerk(index)}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}

      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-muted-foreground"
        onClick={addPerk}
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        Add Perk
      </Button>
    </div>
  );
}
